/**
 * Employee Status Utilities
 *
 * Centralized utilities for checking employee resignation/termination status.
 * These handle legacy sync issues between different status properties.
 *
 * NOTE: Due to legacy data issues, we need to check multiple properties:
 * - states.resigned (boolean) - may not always be set correctly
 * - states._resignation.status (string) - workflow status
 * - states._resignation.last_date - resignation date
 *
 * These properties can get out of sync, so we check all of them.
 */

interface EmployeeStates {
  resigned?: boolean;
  terminated?: boolean;
  resignation_request?: boolean;
  termination_request?: boolean;
  _resignation?: {
    status?: string;
    last_date?: string | Date | null;
  };
}

interface Employee {
  states?: EmployeeStates;
  _resignation?: {
    status?: string;
  };
  active?: boolean;
}

/**
 * Check if an employee has resigned.
 * Handles legacy sync issues by checking multiple properties.
 *
 * @param employee - The employee object to check
 * @returns true if the employee has resigned (or has an active resignation)
 */
export const hasResigned = (employee: Employee | null | undefined): boolean => {
  if (!employee) {
    return false;
  }

  return !!(
    employee.states?.resigned ||
    employee.states?._resignation?.status ||
    employee.states?._resignation?.last_date ||
    (employee._resignation?.status && employee._resignation?.status !== `cancelled`)
  );
};

/**
 * Check if an employee has an active resignation that's not cancelled.
 * More specific check that excludes cancelled resignations.
 *
 * @param employee - The employee object to check
 * @returns true if the employee has resigned (excluding cancelled)
 */
export const hasActiveResignation = (employee: Employee | null | undefined): boolean => {
  if (!employee) {
    return false;
  }

  return !!(
    employee.states?.resigned ||
    (employee.states?._resignation?.status &&
      employee.states?._resignation?.status !== `cancelled`)
  );
};

/**
 * Check if an employee is terminated.
 *
 * @param employee - The employee object to check
 * @returns true if the employee is terminated
 */
export const isTerminated = (employee: Employee | null | undefined): boolean => {
  if (!employee) {
    return false;
  }

  return !!employee.states?.terminated;
};

/**
 * Check if an employee is a former employee (resigned or terminated).
 *
 * @param employee - The employee object to check
 * @returns true if the employee is no longer active
 */
export const isFormerEmployee = (employee: Employee | null | undefined): boolean => {
  return hasResigned(employee) || isTerminated(employee);
};

/**
 * Check if an employee has a pending end-of-employment request.
 *
 * @param employee - The employee object to check
 * @returns true if there's a pending resignation or termination request
 */
export const hasEoeRequest = (employee: Employee | null | undefined): boolean => {
  if (!employee) {
    return false;
  }

  return !!(
    employee.states?.resignation_request ||
    employee.states?.termination_request
  );
};

/**
 * Check if an employee has a resignation in progress (early stages).
 * Used for filtering employees from policy distribution, etc.
 *
 * @param employee - The employee object to check
 * @returns true if resignation is 'started' or 'awaiting approval'
 */
export const hasResignationInProgress = (employee: Employee | null | undefined): boolean => {
  if (!employee) {
    return false;
  }

  const status = employee.states?._resignation?.status;
  return !!status && [`started`, `awaiting approval`].includes(status);
};

export default {
  hasResigned,
  hasActiveResignation,
  isTerminated,
  isFormerEmployee,
  hasEoeRequest,
  hasResignationInProgress,
};
